import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent, ThemeEvents } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {

  connectedCallback() {
    super.connectedCallback();

    this.updateButtonStates();

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }
  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {

    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    const step = parseInt(this.refs.quantityInput.step);
    const newValue = parseInt(this.refs.quantityInput.value) + step;

    this.refs.quantityInput.value = newValue.toString();
    
    this.updateButtonStates();
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    const step = parseInt(this.refs.quantityInput.step);
    const newValue = parseInt(this.refs.quantityInput.value) - step;

    this.refs.quantityInput.value = newValue.toString();
    this.updateButtonStates();
    this.#onQuantityChange();
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    if (event.target instanceof HTMLInputElement) {
      this.refs.quantityInput.value = event.target.value;
    }
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity change event.
   */
  #onQuantityChange() {
    const { quantityInput } = this.refs;

    this.#checkQuantityRules();
    const newValue = parseInt(quantityInput.value);
    this.updateButtonStates();

    quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine)));
  }

  /**
   * Handles cart updates to limit quantity available to add to cart
   */
  #onCartUpdate = () => {
    const nonCartQuantitySelectors = document.querySelectorAll('quantity-selector-component:not(cart-items-component quantity-selector-component)');
    nonCartQuantitySelectors.forEach(selector => {
      if(!(selector instanceof QuantitySelectorComponent)) return;
      window.setTimeout(() => {
        selector.updateButtonStates();
      }, 100);
    });
  }

  /**
   * Updates the quantity button states.
   */
  updateButtonStates() {
    const { quantityInput } = this.refs;
    const { step, value } = quantityInput;

    const plusButton = this.querySelector('.quantity-plus');
    const minusButton = this.querySelector('.quantity-minus');

    if (!quantityInput || !plusButton || !minusButton) return;
    if (!(plusButton instanceof HTMLButtonElement) || !(minusButton instanceof HTMLButtonElement)) return;

    const isInCart = this.closest('cart-items-component');
    const gramsInCart = this.getGramsInCart();
    const monthlyLimit = this.getMonthlyLimit();
    const remainingGrams = monthlyLimit - gramsInCart;
    
    const wouldExceedLimit = isInCart ? remainingGrams < parseInt(step) : remainingGrams < parseInt(value) + parseInt(step);


    plusButton.disabled = wouldExceedLimit;
    plusButton.setAttribute('aria-label', wouldExceedLimit ? 'Monthly limit reached' : 'Increase quantity');

    const isAtMinimum = parseInt(value)  === parseInt(step);
    minusButton.disabled = isAtMinimum;
    minusButton.setAttribute('aria-label', isAtMinimum ? 'Minimum quantity reached' : 'Decrease quantity');
  }

    /**
   * Get current grams in cart
   */
    getGramsInCart() {
      const cartItems = /** @type {HTMLElement} */ (document.querySelector('.cart-items__wrapper'));
      return parseInt(cartItems?.dataset?.totalGrams || '0');
    }
  
    /**
     * Get monthly limit
     */
    getMonthlyLimit() {
      const cartItems = /** @type {HTMLElement} */ (document.querySelector('.cart-items__wrapper'));
      return parseInt(cartItems?.dataset?.monthlyLimit || '100');
    }

  /**
   * Checks the quantity rules are met
   */
  #checkQuantityRules = () => {
    const { quantityInput } = this.refs;
    const { min, max, value: newValue } = quantityInput;

    if (newValue < min && min) quantityInput.value = min;
    if (newValue > max && max) quantityInput.value = max;
  };

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
